import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { ROTULO_PAPEL, definirLotacao, useUnidades } from "../unidades/dados";
import { gestoresDaLista, idDaPessoa, papelDe, type Gestor, type PessoaDaLista } from "./gestaoPessoas";

const NENHUM = "__nenhum__";

/**
 * Função, unidade e gerente de uma pessoa (migration 308). Abre para super
 * admin, administrador e responsável pela unidade; o banco confere se quem
 * salva pode mexer nesta pessoa e dar esta função.
 */
export const LotacaoDialog = ({
  pessoa,
  pessoas,
  papeis,
  unidadeFixa,
  onClose,
  onSaved,
}: {
  pessoa: PessoaDaLista;
  /** A lista da tela, para montar os gerentes possíveis. */
  pessoas: PessoaDaLista[];
  /** Funções que quem edita pode dar. */
  papeis: string[];
  /** Responsável pela unidade: a unidade não muda. */
  unidadeFixa: string | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const papelAtual = papelDe(pessoa);
  const { data: unidades } = useUnidades();
  const [crmId, setCrmId] = useState<number | null>(null);
  const [papel, setPapel] = useState(papelAtual);
  const [unidade, setUnidade] = useState<string>(NENHUM);
  const [gerente, setGerente] = useState<string>(NENHUM);
  const [gestores, setGestores] = useState<Gestor[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void Promise.all([idDaPessoa(pessoa.id), gestoresDaLista(pessoas)]).then(([linha, lista]) => {
      if (!vivo) return;
      setCrmId(linha?.crmId ?? null);
      setUnidade(linha?.unidadeId ?? unidadeFixa ?? NENHUM);
      setGerente(linha?.managerId ? String(linha.managerId) : NENHUM);
      setGestores(lista.filter((g) => g.crmId !== linha?.crmId));
      setCarregando(false);
    });
    return () => {
      vivo = false;
    };
  }, [pessoa.id, pessoas, unidadeFixa]);

  const podeMudarPapel = papeis.includes(papelAtual);
  const opcoesPapel = podeMudarPapel ? papeis : [papelAtual];
  const unidadesAtivas = (unidades ?? []).filter((u) => u.ativa || u.id === unidade);
  // Gerente da mesma unidade; sem unidade escolhida, qualquer um da lista.
  const gestoresDaUnidade = useMemo(
    () => gestores.filter((g) => unidade === NENHUM || g.unidadeId === unidade || String(g.crmId) === gerente),
    [gestores, unidade, gerente],
  );

  const gravar = async () => {
    if (crmId == null) {
      setErro("Pessoa sem cadastro completo. É necessário o primeiro acesso.");
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      if (papel !== papelAtual) {
        const { error } = await supabase.rpc("update_user_role", { p_crm_user_id: crmId, p_role_code: papel });
        if (error) throw new Error(`Não foi possível mudar a função: ${error.message}`);
      }
      await definirLotacao(crmId, unidade === NENHUM ? null : unidade, gerente === NENHUM ? null : Number(gerente));
      onSaved();
      onClose();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Unidade e equipe</DialogTitle>
          <DialogDescription className="break-all">{pessoa.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Função</Label>
            <Select value={papel} onValueChange={setPapel} disabled={!podeMudarPapel || carregando}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {opcoesPapel.map((p) => (
                  <SelectItem key={p} value={p}>{ROTULO_PAPEL[p] ?? p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Unidade</Label>
            <Select value={unidade} onValueChange={setUnidade} disabled={!!unidadeFixa || carregando}>
              <SelectTrigger><SelectValue placeholder="Selecione a unidade" /></SelectTrigger>
              <SelectContent>
                {!unidadeFixa && <SelectItem value={NENHUM}>Sem unidade</SelectItem>}
                {unidadesAtivas.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Gerente</Label>
            <Select value={gerente} onValueChange={setGerente} disabled={carregando}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NENHUM}>Sem gerente</SelectItem>
                {gestoresDaUnidade.map((g) => (
                  <SelectItem key={g.crmId} value={String(g.crmId)}>{g.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {erro && <p className="text-sm text-destructive">{erro}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={salvando}>Cancelar</Button>
          <Button className="bg-marinho" onClick={gravar} disabled={salvando || carregando}>
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
